export function formatDate(dateString: string | Date) {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

// Parses a value that may be a bare calendar date ("2026-01-15") or a full
// timestamp, returning null when it is empty or unparseable.
//
// A bare YYYY-MM-DD is parsed at LOCAL midnight, not UTC. `new Date("2026-01-15")`
// is specified to parse as UTC midnight, which at any negative offset is the
// PREVIOUS day locally - so `.getDate()` returns 14 in New York. Building the
// date from components avoids that. Anything else falls through to the normal
// Date parser, so full ISO timestamps keep their exact current behaviour.
export function parseDateValue(value: string | Date): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const [, year, month, day] = dateOnly.map(Number);
    const parsed = new Date(year, month - 1, day);
    // Round-trip the components: JS rolls overflow over ("2026-01-32" becomes
    // 1 Feb) and treats a 2-digit year as 19xx, so reject rather than render a
    // date the caller did not mean.
    const roundTrips =
      parsed.getFullYear() === year &&
      parsed.getMonth() === month - 1 &&
      parsed.getDate() === day;
    return roundTrips ? parsed : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// Anchors a locally-selected calendar day to its LOCAL start/end instant
// before sending it to the API as an ISO string. A bare `date.toISOString()`
// on a `new Date(year, month, day)` (local midnight) is NOT what the user
// picked once converted to UTC - e.g. local midnight in UTC+2 is 22:00 the
// PREVIOUS day in UTC, so an unadjusted end date silently excludes every
// event from later that same local day (and, for a single-day selection
// where start === end, produces a zero-width range that matches nothing).
export function toStartOfDayISOString(date: Date): string {
  const startOfDay = new Date(date);
  startOfDay.setHours(0, 0, 0, 0);
  return startOfDay.toISOString();
}

export function toEndOfDayISOString(date: Date): string {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}

export function formatDateOnly(dateString: string | Date): string {
  const date = parseDateValue(dateString);
  if (!date) return "";

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatFullDate(dateString: string | Date): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
